/**
 * Tier-gated event point lookups (Phase 5) — get_events_by_ids /
 * get_upcoming_events_for_venue. Staffelung via discovery_item_unlocked_for_tier;
 * locked rows return teaser + unlock_at (never silent 404 when the row exists).
 */
import { supabase } from "@/lib/supabase";
import {
  assertDiscoveryTeaserShape,
  type DiscoveryTeaserRow,
} from "@/lib/discoveryFeed";

export type EventAccessTeaserRow = DiscoveryTeaserRow & {
  unlock_at: string | null;
  visible: false;
  payload_kind: "teaser";
};

export type EventAccessFullRow = Record<string, unknown> & {
  id: string;
  payload_kind: "full";
  visible: true;
  unlock_at?: string | null;
};

export type EventAccessItem = EventAccessFullRow | EventAccessTeaserRow;

const TEASER_PLUS_UNLOCK = new Set([
  "id",
  "category",
  "teaser_image_url",
  "is_top_pick",
  "unlock_at",
  "visible",
  "payload_kind",
  "kind",
]);

export function assertEventAccessTeaserShape(row: Record<string, unknown>): EventAccessTeaserRow {
  const unexpected = Object.keys(row).filter((k) => !TEASER_PLUS_UNLOCK.has(k));
  if (unexpected.length > 0) {
    throw new Error(`Event access teaser leaked keys: ${unexpected.join(", ")}`);
  }
  const base = assertDiscoveryTeaserShape({
    id: row.id,
    category: row.category,
    teaser_image_url: row.teaser_image_url,
    is_top_pick: row.is_top_pick,
  });
  return {
    ...base,
    unlock_at: row.unlock_at == null ? null : String(row.unlock_at),
    visible: false,
    payload_kind: "teaser",
  };
}

function parseAccessItem(raw: unknown): EventAccessItem | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const kind = String(row.payload_kind ?? "");
  if (kind === "teaser" || row.visible === false) {
    return assertEventAccessTeaserShape(row);
  }
  if (kind === "full" || row.visible === true) {
    return {
      ...row,
      id: String(row.id),
      payload_kind: "full",
      visible: true,
      unlock_at: row.unlock_at == null ? null : String(row.unlock_at),
    };
  }
  return null;
}

export async function rpcGetEventsByIds(
  ids: string[],
  options?: { asOf?: string | null; tierOverride?: string | null },
): Promise<{ tier: string; asOf: string; events: EventAccessItem[] }> {
  const unique = [...new Set(ids.filter(Boolean))];
  if (unique.length === 0) {
    return { tier: "", asOf: "", events: [] };
  }

  const { data, error } = await supabase.rpc("get_events_by_ids", {
    p_ids: unique,
    p_as_of: options?.asOf ?? new Date().toISOString(),
    p_tier_override: options?.tierOverride ?? null,
  });
  if (error) throw error;

  const raw = (data ?? {}) as Record<string, unknown>;
  const list = Array.isArray(raw.events) ? raw.events : [];
  return {
    tier: String(raw.tier ?? ""),
    asOf: String(raw.as_of ?? ""),
    events: list.map(parseAccessItem).filter((e): e is EventAccessItem => e != null),
  };
}

export type VenueUpcomingAccess = {
  tier: string;
  asOf: string;
  venueId: string;
  events: EventAccessItem[];
  visibleCount: number;
  totalCount: number;
};

export async function rpcGetUpcomingEventsForVenue(
  venueId: string,
  options?: { asOf?: string | null; tierOverride?: string | null },
): Promise<VenueUpcomingAccess> {
  const { data, error } = await supabase.rpc("get_upcoming_events_for_venue", {
    p_venue_id: venueId,
    p_as_of: options?.asOf ?? new Date().toISOString(),
    p_tier_override: options?.tierOverride ?? null,
  });
  if (error) throw error;

  const raw = (data ?? {}) as Record<string, unknown>;
  const list = Array.isArray(raw.events) ? raw.events : [];

  return {
    tier: String(raw.tier ?? ""),
    asOf: String(raw.as_of ?? ""),
    venueId: String(raw.venue_id ?? venueId),
    events: list.map(parseAccessItem).filter((e): e is EventAccessItem => e != null),
    visibleCount: Number(raw.visible_count ?? 0),
    totalCount: Number(raw.total_count ?? list.length),
  };
}
